import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Brain, Zap, Heart, Computer, Anchor } from 'lucide-react';
import { toast } from 'sonner';

function QuadruneBrainGame({ onBack }) {
  const [activeArea, setActiveArea] = useState(null);
  const [currentScenario, setCurrentScenario] = useState('');
  const [userResponse, setUserResponse] = useState('');
  const [score, setScore] = useState({ lizard: 0, mammal: 0, computer: 0, porpoise: 0 });

  const brainAreas = [
    {
      id: 'lizard',
      name: '🦎 Lizard Legacy',
      title: 'Reptilian Brain (Survival)',
      description: 'Safety, sustenance, sex - your ancient survival instincts',
      color: 'bg-red-500',
      scenarios: [
        'You smell fresh pizza and immediately feel hungry',
        'A car honks loudly and you jump',
        'You see an attractive person and feel drawn to them',
        'You feel tired and want to rest immediately'
      ],
      questions: [
        'What reptilian instincts helped your ancestors survive but now cause problems?',
        'When do you act on impulse without thinking?',
        'What automatic reactions do you have to food, safety, or attraction?'
      ]
    },
    {
      id: 'mammal',
      name: '🐾 Furry Li\'l Mammal', 
      title: 'Limbic System (Emotions)',
      description: 'Bonding, status, play - your social and emotional instincts',
      color: 'bg-pink-500',
      scenarios: [
        'Someone doesn\'t invite you to their party and you feel left out',
        'You see others laughing and wonder if they\'re talking about you',
        'A friend gets a promotion and you feel competitive',
        'You want to play and have fun instead of working'
      ],
      questions: [
        'When do you seek status or worry about your social position?',
        'How do you bond with others? What triggers your need for connection?',
        'When does your inner child want to come out and play?'
      ]
    },
    {
      id: 'computer',
      name: '🤖 Computer Brain',
      title: 'Neocortex (Thinking)', 
      description: 'Meaning, morality, making sense - your interpretive mind',
      color: 'bg-blue-500',
      scenarios: [
        'Someone cancels plans and you create a story about why',
        'You judge someone\'s outfit as "unprofessional"',
        'You analyze someone\'s behavior and decide they don\'t like you',
        'You make rules about how things "should" be'
      ],
      questions: [
        'What meanings do you automatically create about events?',
        'When do you judge situations as right/wrong, good/bad?',
        'What stories do you tell yourself that may not be true?'
      ]
    },
    {
      id: 'porpoise',
      name: '🐬 Higher Porpoise',
      title: 'Prefrontal Cortex (Wisdom)',
      description: 'Wholeness, transcendence, contribution - your highest self',
      color: 'bg-purple-500', 
      scenarios: [
        'You see conflict as an opportunity for deeper understanding',
        'You feel grateful for a difficult experience because it taught you something',
        'You choose to respond with love even when someone is unkind',
        'You act for the benefit of future generations'
      ],
      questions: [
        'When do you act from wisdom rather than reactivity?',
        'How do you contribute to something larger than yourself?',
        'When have "bad" things turned out to be blessings?'
      ]
    }
  ];

  const exploreArea = (area) => {
    setActiveArea(area);
    const randomScenario = area.scenarios[Math.floor(Math.random() * area.scenarios.length)];
    setCurrentScenario(randomScenario);
    setUserResponse('');
    
    // Play brain area sound
    const tones = {
      lizard: 'UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAxMTExMTExMTExMTExMTExMTEx',
      mammal: 'UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAyMjIyMjIyMjIyMjIyMjIyMjIy',
      computer: 'UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAAzMzMzMzMzMzMzMzMzMzMzMzMz',
      porpoise: 'UklGRj4AAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoAAAA0NDQ0NDQ0NDQ0NDQ0NDQ0NDQ0'
    };
    
    const audio = new Audio(`data:audio/wav;base64,${tones[area.id]}`);
    audio.volume = 0.4;
    audio.play().catch(() => {});
  };

  return (
    <div className="max-w-6xl mx-auto">
      
      {/* Header */}
      <div className="text-center mb-8">
        <Button onClick={onBack} variant="outline" className="border-white text-white hover:bg-white hover:text-black mb-6" style={{ borderRadius: 0 }}>
          ← Back to Games & Tools
        </Button>
        <h1 className="text-4xl font-bold text-white mb-4">🧠 Quadrune Brain Explorer</h1>
        <p className="text-xl text-blue-200">Understand your mind to make more conscious choices</p>
      </div>

      {/* Brain Areas Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {brainAreas.map((area) => (
          <Card 
            key={area.id}
            className={`border-4 cursor-pointer transition-all hover:scale-105 ${
              activeArea?.id === area.id ? 'border-white' : 'border-gray-400'
            }`} 
            style={{ borderRadius: 0 }}
            onClick={() => exploreArea(area)}
          >
            <CardHeader className={`${area.color} text-white`}>
              <CardTitle className="text-xl">{area.name}</CardTitle>
              <p className="text-sm opacity-90">{area.title}</p>
            </CardHeader>
            <CardContent className="p-4 bg-white">
              <p className="text-sm text-gray-700">{area.description}</p>
              <Button 
                className={`w-full mt-3 ${area.color} text-white hover:opacity-90`}
                style={{ borderRadius: 0 }}
              >
                🔍 Explore {area.name}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Active Scenario */}
      {activeArea && (
        <Card className="border-4 border-white bg-white" style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-2xl text-gray-800">
              Exploring Your {activeArea.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 p-6 border-l-4 border-gray-400">
              <p className="text-lg font-bold mb-2">Scenario:</p>
              <p className="text-gray-800 italic">"{currentScenario}"</p>
            </div>
            
            <div>
              <label className="block text-sm font-bold mb-2">
                What thoughts, feelings, or reactions does this bring up for you?
              </label>
              <Textarea
                placeholder="Notice what happens in your mind and body..."
                value={userResponse}
                onChange={(e) => setUserResponse(e.target.value)}
                className="border-gray-500 min-h-[100px]"
                style={{ borderRadius: 0 }}
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  if (userResponse.trim()) {
                    setScore(prev => ({ ...prev, [activeArea.id]: prev[activeArea.id] + 1 }));
                    toast.success(`${activeArea.name} awareness +1! You're becoming more conscious.`);
                    setUserResponse('');
                    exploreArea(activeArea); // Generate new scenario
                  }
                }}
                className={`${activeArea.color} text-white hover:opacity-90 flex-1`}
                style={{ borderRadius: 0 }}
                disabled={!userResponse.trim()}
              >
                ✨ Record Insight
              </Button>
              <Button 
                onClick={() => exploreArea(activeArea)}
                variant="outline"
                className="border-gray-500"
                style={{ borderRadius: 0 }}
              >
                🎲 New Scenario
              </Button>
            </div>
            
            {/* Questions for Reflection */}
            <div className="bg-blue-50 p-4 border-l-4 border-blue-500">
              <h4 className="font-bold text-blue-800 mb-2">Reflection Questions:</h4>
              <div className="space-y-1">
                {activeArea.questions.map((question, i) => (
                  <p key={i} className="text-sm text-blue-700">• {question}</p>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Score Display */}
      <Card className="mt-8 border-4 border-white bg-white" style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-xl text-center">🏆 Brain Awareness Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl">🦎</p>
              <p className="font-bold">{score.lizard}</p>
              <p className="text-xs">Lizard</p>
            </div>
            <div>
              <p className="text-2xl">🐾</p>
              <p className="font-bold">{score.mammal}</p>
              <p className="text-xs">Mammal</p>
            </div>
            <div>
              <p className="text-2xl">🤖</p>
              <p className="font-bold">{score.computer}</p>
              <p className="text-xs">Computer</p>
            </div>
            <div>
              <p className="text-2xl">🐬</p>
              <p className="font-bold">{score.porpoise}</p>
              <p className="text-xs">Porpoise</p>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

export default QuadruneBrainGame;